<template>
    <v-container class="mt-10 pb-12"
        style="min-width: 1200px; max-width: 1600px; padding-left: 24px; padding-right: 24px;">
        <!-- Header Section -->
        <div class="mb-6">
            <div class="d-flex align-center justify-space-between mb-4">
                <div>
                    <h1 class="text-h4 font-weight-bold mb-2">Personal Resource Library</h1>
                    <p class="text-subtitle-1 grey--text mb-0">Access and manage your personal documents and resources</p>
                </div>
                <div class="d-flex" style="gap: 8px;">
                    <v-btn color="primary" outlined :loading="refreshing" @click="refreshData" class="text-none">
                        <v-icon left>mdi-refresh</v-icon>
                        Refresh
                    </v-btn>
                    <v-btn v-if="canUploadDocuments" color="primary" @click="showUploadDialog = true" class="text-none">
                        <v-icon left>mdi-upload</v-icon>
                        Upload Document
                    </v-btn>
                </div>
            </div>
        </div>

        <!-- Filters Section -->
        <v-card class="mb-6 pa-4" outlined>
            <v-row align="center" no-gutters>
                <!-- Search Bar -->
                <v-col cols="12" md="4" class="pr-md-3 mb-3 mb-md-0">
                    <v-text-field v-model="searchTerm" prepend-inner-icon="mdi-magnify" label="Search resources..."
                        outlined dense hide-details clearable class="search-field"></v-text-field>
                </v-col>

                <!-- Company Filter -->
                <v-col cols="12" md="3" class="px-md-2 mb-3 mb-md-0">
                    <v-select v-model="selectedCompany" :items="companyOptions" label="Company" outlined dense
                        hide-details class="company-select"></v-select>
                </v-col>

                <!-- Show Pinned Only -->
                <v-col cols="12" md="2" class="px-md-2 mb-3 mb-md-0">
                    <v-switch v-model="showPinnedOnly" label="Pinned Only" hide-details
                        class="pinned-switch"></v-switch>
                </v-col>

                <!-- Clear Filters -->
                <v-col cols="12" md="3" class="pl-md-2">
                    <v-btn text small @click="clearFilters" class="clear-filters-btn">
                        <v-icon left small>mdi-filter-off</v-icon>
                        Clear Filters
                    </v-btn>
                </v-col>
            </v-row>

            <!-- Category Filter Buttons -->
            <v-row class="mt-3">
                <v-col cols="12">
                    <div class="category-filters">
                        <v-btn-toggle v-model="selectedCategory" mandatory color="primary" class="category-toggle">
                            <v-btn v-for="category in categories" :key="category" :value="category" small
                                class="category-btn">
                                {{ category }}
                            </v-btn>
                        </v-btn-toggle>
                    </div>
                </v-col>
            </v-row>
        </v-card>

        <!-- Loading State -->
        <v-card v-if="loading" class="pa-6 text-center">
            <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
            <p class="mt-3 text-subtitle-1">Loading resources...</p>
        </v-card>

        <!-- Error State -->
        <v-alert v-if="error" type="error" dismissible @input="clearErrorMessage" class="mb-4">
            {{ error }}
        </v-alert>

        <!-- No Results State -->
        <v-card v-if="!loading && filteredResourcesList.length === 0" class="pa-6 text-center">
            <v-icon size="64" color="grey lighten-1" class="mb-3">mdi-file-search-outline</v-icon>
            <h3 class="text-h6 grey--text mb-2">No resources found</h3>
            <p class="text-body-2 grey--text">
                {{ searchTerm || selectedCategory !== 'All' || selectedCompany !== 'all' ? 'Try adjusting your filters'
                    : 'No resources available' }}
            </p>
        </v-card>

        <!-- Grouped Resource Cards -->
        <div v-if="!loading && groupedResources.length > 0">
            <div v-for="(group, groupIndex) in groupedResources" :key="groupIndex" class="mb-8">
                <!-- Group Header -->
                <div class="d-flex align-center mb-4 group-header">
                    <v-icon :color="group.color" size="28" class="mr-3">{{ group.icon }}</v-icon>
                    <div>
                        <h2 class="text-h6 font-weight-bold mb-0">{{ group.title }}</h2>
                        <p class="text-caption grey--text mb-0">{{ group.subtitle }}</p>
                    </div>
                    <v-spacer></v-spacer>
                    <v-chip small :color="group.color" text-color="white">
                        {{ group.resources.length }}
                    </v-chip>
                </div>

                <!-- Resource Cards Grid for this group -->
                <v-row>
                    <v-col v-for="resource in group.resources" :key="resource.id" cols="12" sm="6" md="4" lg="3">
                        <personal-resource-card :resource="resource" @pin-toggle="handlePinToggle" @download="handleDownload"
                            @edit="handleEdit" @delete="confirmDelete" />
                    </v-col>
                </v-row>
            </div>
        </div>

        <!-- Upload Dialog -->
        <upload-personal-document-dialog ref="uploadDialog" v-model="showUploadDialog" @upload-document="handleDocumentUpload" />

        <!-- Edit Resource Dialog -->
        <edit-personal-resource-dialog v-if="resourceToEdit" v-model="showEditDialog" :resource="resourceToEdit"
            :companies="companies()" @update-resource="handleUpdateResource" />

        <!-- Delete Confirmation Dialog -->
        <v-dialog v-model="showDeleteDialog" max-width="500px">
            <v-card>
                <v-card-title class="headline red white--text">
                    <v-icon left color="white">mdi-delete-alert</v-icon>
                    Confirm Delete
                </v-card-title>

                <v-card-text class="pa-6">
                    <div class="text-center">
                        <v-icon size="64" color="red lighten-1" class="mb-4">mdi-file-remove</v-icon>
                        <h3 class="text-h6 mb-3">Delete Resource</h3>
                        <p class="text-body-1 mb-4">
                            Are you sure you want to delete
                            <strong>"{{ resourceToDelete?.name }}"</strong>?
                        </p>
                        <p class="text-body-2 red--text">
                            This action cannot be undone.
                        </p>
                    </div>
                </v-card-text>

                <v-card-actions class="px-6 pb-6">
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelDelete" class="mr-2">
                        Cancel
                    </v-btn>
                    <v-btn color="red" @click="confirmDeleteResource" :loading="loading">
                        <v-icon left>mdi-delete</v-icon>
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Success/Error Snackbars -->
        <v-snackbar v-model="showSuccessSnackbar" color="success" timeout="3000" bottom>
            {{ successMessage }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="showSuccessSnackbar = false">
                    Close
                </v-btn>
            </template>
        </v-snackbar>

        <v-snackbar v-model="showErrorSnackbar" color="error" timeout="5000" bottom>
            {{ errorMessage }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="showErrorSnackbar = false">
                    Close
                </v-btn>
            </template>
        </v-snackbar>
    </v-container>
</template>

<script>
import PersonalResourceCard from './components/PersonalResourceCard.vue';
import UploadPersonalDocumentDialog from './dialogs/UploadPersonalDocumentDialog.vue';
import EditPersonalResourceDialog from './dialogs/EditPersonalResourceDialog.vue';

export default {
    name: 'ResourceComponent',
    inject: ['personalResources', 'refreshPersonalResources', 'addPersonalResource', 'updatePersonalResource', 'removePersonalResource', 'companies'],
    components: {
        PersonalResourceCard,
        UploadPersonalDocumentDialog,
        EditPersonalResourceDialog
    },
    data() {
        return {
            searchTerm: '',
            selectedCategory: 'All',
            selectedCompany: 'all',
            showPinnedOnly: false,
            refreshing: false,
            showUploadDialog: false,
            showDeleteDialog: false,
            showEditDialog: false,
            resourceToDelete: null,
            resourceToEdit: null,
            showSuccessSnackbar: false,
            showErrorSnackbar: false,
            successMessage: '',
            errorMessage: '',
            // Local loading and error state
            loading: false,
            error: null
        };
    },
    computed: {
        get_user() {
            return this.$store.getters['auth/get_user'];
        },
        canUploadDocuments() {
            // Allow specific user IDs
            const allowedUserIds = [1, 177, 145];
            if (allowedUserIds.includes(this.get_user?.id)) {
                return true;
            }

            // Allow specific role IDs (1=Admin, 2=Manager)
            const allowedRoleIds = [1, 2];
            return allowedRoleIds.includes(this.get_user?.role_id);
        },
        categories() {
            return ['All', 'SOP', 'Template', 'EOD', 'SOD', 'Guide', 'Other'];
        },
        companyOptions() {
            const companies = this.companies() || [];

            // Exception: Specific user IDs can see all companies
            const privilegedUserIds = [1, 177, 145];
            const isPrivilegedUser = this.get_user && privilegedUserIds.includes(this.get_user.id);

            let accessibleCompanies;
            if (isPrivilegedUser) {
                // Privileged users can see all companies
                accessibleCompanies = companies;
            } else {
                // Filter companies based on user access
                accessibleCompanies = companies.filter(company => {
                    // Check if user has access to this company
                    if (!this.get_user || !this.get_user.access || !Array.isArray(this.get_user.access)) {
                        return false;
                    }
                    return this.get_user.access.some(access => access.company_id === company.id);
                });
            }

            const options = [
                { text: 'All Companies', value: 'all' },
                ...accessibleCompanies.map(company => ({
                    text: company.name,
                    value: company.id
                })),
                { text: 'Shared Resources', value: null }
            ];
            return options;
        },
        filteredResourcesList() {
            // Get resources from injection and apply filters locally
            const allResources = this.personalResources() || [];

            return allResources.filter(resource => {
                // Only show resources uploaded directly via Resource tab
                // Exclude task attachments and project-specific resources
                //JOO CHANGE
                // if (resource.source && resource.source !== 'resource_tab') {
                //     return false;
                // }
                
                // Access control: Check if user can access this resource
                if (!this.hasAccessToResource(resource)) {
                    return false;
                }

                // Company filter
                if (this.selectedCompany !== 'all') {
                    if (this.selectedCompany === null && resource.company_id !== null) return false;
                    if (this.selectedCompany !== null && resource.company_id !== this.selectedCompany) return false;
                }

                // Category filter
                if (this.selectedCategory !== 'All' && resource.category !== this.selectedCategory) {
                    return false;
                }

                // Search filter
                if (this.searchTerm) {
                    const searchLower = this.searchTerm.toLowerCase();
                    const nameMatch = resource.name && resource.name.toLowerCase().includes(searchLower);
                    const descMatch = resource.description && resource.description.toLowerCase().includes(searchLower);
                    if (!nameMatch && !descMatch) return false;
                }

                // Pinned filter
                if (this.showPinnedOnly && !resource.pinned) {
                    return false;
                }

                return true;
            });
        },
        groupedResources() {
            const filtered = this.filteredResourcesList;
            const companies = this.companies() || [];
            const groups = [];

            // First group: Shared resources (no company_id)
            const sharedResources = filtered.filter(resource => !resource.company_id);
            if (sharedResources.length > 0) {
                // Sort shared resources: pinned first, then by name
                const sortedSharedResources = [...sharedResources].sort((a, b) => {
                    // First sort by pinned status (pinned items first)
                    if (a.pinned !== b.pinned) {
                        return b.pinned ? 1 : -1;
                    }
                    // Then sort by name
                    return (a.name || '').localeCompare(b.name || '');
                });
                
                groups.push({
                    title: 'Your Resources',
                    subtitle: 'Available to all companies',
                    icon: 'mdi-share-variant',
                    color: 'blue',
                    resources: sortedSharedResources
                });
            }

            // Exception: Specific user IDs can see all companies
            const privilegedUserIds = [1, 177, 145];
            const isPrivilegedUser = this.get_user && privilegedUserIds.includes(this.get_user.id);

            let accessibleCompanies;
            if (isPrivilegedUser) {
                // Privileged users can see all companies
                accessibleCompanies = companies;
            } else {
                // Filter companies based on user access
                accessibleCompanies = companies.filter(company => {
                    // Check if user has access to this company
                    if (!this.get_user || !this.get_user.access || !Array.isArray(this.get_user.access)) {
                        return false;
                    }
                    return this.get_user.access.some(access => access.company_id === company.id);
                });
            }

            // Group by accessible companies
            const companiesWithResources = accessibleCompanies
                .map(company => {
                    const companyResources = filtered.filter(resource => resource.company_id === company.id);
                    return {
                        company,
                        resources: companyResources
                    };
                })
                .filter(group => group.resources.length > 0);

            // Add company groups
            companiesWithResources.forEach(({ company, resources }) => {
                // Sort company resources: pinned first, then by name
                const sortedCompanyResources = [...resources].sort((a, b) => {
                    // First sort by pinned status (pinned items first)
                    if (a.pinned !== b.pinned) {
                        return b.pinned ? 1 : -1;
                    }
                    // Then sort by name
                    return (a.name || '').localeCompare(b.name || '');
                });
                
                groups.push({
                    title: company.name,
                    subtitle: `${resources.length} resource${resources.length === 1 ? '' : 's'}`,
                    icon: 'mdi-domain',
                    color: 'primary',
                    resources: sortedCompanyResources
                });
            });

            return groups;
        }
    },
    async mounted() {
        // Check if resources are already loaded from DefaultLayout
        const hasResources = (this.personalResources() || []).length > 0;

        // Only fetch if not already loaded
        if (!hasResources) {
            await this.fetchResources();
        }
    },
    methods: {
        setLoading(loading) {
            this.loading = loading;
        },
        setError(error) {
            this.error = error;
        },
        clearError() {
            this.error = null;
        },

        hasAccessToResource(resource) {
            // Exception: Specific user IDs can see all resources
            const privilegedUserIds = [1, 177, 145];
            if (this.get_user && privilegedUserIds.includes(this.get_user.id)) {
                return true;
            }

            // Always allow access to shared resources (no company_id)
            if (!resource.company_id) {
                return true;
            }

            // Check if user exists and has access array
            if (!this.get_user || !this.get_user.access || !Array.isArray(this.get_user.access)) {
                return false;
            }

            // Check if user has access to the resource's company
            return this.get_user.access.some(access => access.company_id === resource.company_id);
        },

        async fetchResources() {
            this.setLoading(true);
            this.clearError();
            try {
                // Use DefaultLayout's refresh method
                if (typeof this.refreshPersonalResources === 'function') {
                    await this.refreshPersonalResources();
                }
            } catch (error) {
                this.setError(error.message);
                this.showError('Failed to load resources');
            } finally {
                this.setLoading(false);
            }
        },

        async handlePinToggle(resourceId) {
            const resource = (this.personalResources() || []).find(r => r.id === resourceId);
            if (!resource) return;

            const newPinnedState = !resource.pinned;

            try {
                // Optimistic update using provider pattern
                this.updatePersonalResource({ resourceId, updatedData: { pinned: newPinnedState } });
                
                // Show immediate feedback
                this.showSuccess(`Resource ${newPinnedState ? 'pinned' : 'unpinned'} successfully`);

                const response = await this.$axios.patch(`/api/personal_resources/${resourceId}/pin`, {
                    pinned: newPinnedState
                });

                if (response.data.success) {
                    // Update with server data if different
                    if (response.data.data) {
                        this.updatePersonalResource({ resourceId, updatedData: response.data.data });
                    }
                } else {
                    // Revert on API failure
                    this.updatePersonalResource({ resourceId, updatedData: { pinned: resource.pinned } });
                    this.showError(response.data.message || 'Failed to update pin status');
                }
            } catch (error) {
                // Revert on error
                this.updatePersonalResource({ resourceId, updatedData: { pinned: resource.pinned } });
                this.setError(error.message);
                this.showError('Failed to update pin status');
            }
        },

        async handleDownload(resource) {
            // For URL resources, open the URL directly instead of downloading
            if (resource.type === 'url' || (resource.url && !resource.file_path)) {
                window.open(resource.url, '_blank');
                this.showSuccess(`Opened ${resource.name}`);
                return;
            }

            // For file resources, handle download as before
            try {
                const apiBaseUrl = (process.env.VUE_APP_API_URL || '').replace(/\/+$/, '');
                const downloadUrl = `${apiBaseUrl}/api/personal_resources/${resource.id}/download`;

                const response = await this.$axios.get(downloadUrl, {
                    responseType: 'blob'
                });

                // Create download link
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', resource.name);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                this.showSuccess(`Downloaded ${resource.name}`);
            } catch (error) {
                this.setError(error.message);
                this.showError('Failed to download resource');
            }
        },

        async handleDocumentUpload(resourceData) {
            // Optimistic update
            const tempResource = {
                ...resourceData,
                id: 'temp_' + Date.now(),
                created_at: new Date().toISOString(),
                uploading: true
            };

            try {
                this.addPersonalResource(tempResource);

                let response;

                if (resourceData.type === 'file' && resourceData.file) {
                    // Handle file upload with FormData
                    const formData = new FormData();

                    // Append the file
                    formData.append('file', resourceData.file);

                    // Append other form fields
                    formData.append('name', resourceData.name || '');
                    formData.append('description', resourceData.description || '');
                    formData.append('category', resourceData.category || '');
                    formData.append('company_id', resourceData.company_id || '');
                    formData.append('file_type', resourceData.file_type || '');
                    formData.append('size', resourceData.size || '');
                    formData.append('type', resourceData.type || 'file');
                    formData.append('source', 'resource_tab');
                    formData.append('created_by', resourceData.created_by || '');
                    formData.append('user_id', this.get_user.id);

                    // Handle tags array
                    if (resourceData.tags && Array.isArray(resourceData.tags)) {
                        resourceData.tags.forEach((tag, index) => {
                            formData.append(`tags[${index}]`, tag);
                        });
                    } else if (resourceData.tags) {
                        formData.append('tags', resourceData.tags);
                    }

                    response = await this.$axios.post('/api/personal_resources', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        },
                        onUploadProgress: (progressEvent) => {
                            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            // You can emit progress events here if needed
                            console.log(`Upload Progress: ${percentCompleted}%`);
                        }
                    });
                } else if (resourceData.type === 'url') {
                    // Handle URL resource with JSON data
                    const payload = {
                        name: resourceData.name || '',
                        description: resourceData.description || '',
                        category: resourceData.category || '',
                        company_id: resourceData.company_id || null,
                        file_type: resourceData.file_type || 'url',
                        type: 'url',
                        url: resourceData.url,
                        source: 'resource_tab',
                        created_by: resourceData.created_by || '',
                        user_id: this.get_user.id,
                        tags: resourceData.tags || []
                    };

                    response = await this.$axios.post('/api/personal_resources', payload);
                } else {
                    throw new Error('Invalid resource data: missing file or URL');
                }

                if (response.data.success) {
                    // Replace temp resource with actual resource from server
                    this.removePersonalResource(tempResource.id);
                    this.addPersonalResource({ ...response.data.data, uploading: false });

                    // Refresh to sync with server state
                    await this.refreshPersonalResources();

                    // Close dialog and show success message
                    this.showUploadDialog = false;
                    this.showSuccess(`Successfully ${resourceData.type === 'file' ? 'uploaded' : 'added'} ${resourceData.name}`);

                    // Reset the upload form
                    if (this.$refs.uploadDialog) {
                        this.$refs.uploadDialog.resetForm();
                    }

                    return { success: true, data: response.data.data };
                } else {
                    throw new Error(response.data.message || `${resourceData.type === 'file' ? 'Upload' : 'Creation'} failed`);
                }
            } catch (error) {
                // Remove temp resource on error
                this.removePersonalResource(tempResource.id);
                this.setError(error.message);
                throw error;
            }
        },

        async handleResourceUpdate(resourceId, updatedData) {
            // Get original data before update for potential rollback
            const originalResource = (this.personalResources() || []).find(r => r.id === resourceId);
            if (!originalResource) {
                this.showError('Resource not found');
                return { success: false, error: 'Resource not found' };
            }

            try {
                // Optimistic update
                this.updatePersonalResource({ resourceId, updatedData });

                const response = await this.$axios.put(`/api/personal_resources/${resourceId}`, updatedData);
                if (response.data.success) {
                    this.updatePersonalResource({ resourceId, updatedData: response.data.data });
                    this.showSuccess('Resource updated successfully');
                    return { success: true, data: response.data.data };
                } else {
                    throw new Error(response.data.message || 'Update failed');
                }
            } catch (error) {
                // Revert on error
                this.updatePersonalResource({ resourceId, updatedData: originalResource });
                this.setError(error.message);
                this.showError('Failed to update resource');
                return { success: false, error: error.message };
            }
        },

        async handleResourceDelete(resourceId) {
            try {
                const response = await this.$axios.delete(`/api/personal_resources/${resourceId}`);
                if (response.data.success) {
                    this.removePersonalResource(resourceId);
                    this.showSuccess('Resource deleted successfully');
                    return { success: true };
                } else {
                    throw new Error(response.data.message || 'Delete failed');
                }
            } catch (error) {
                this.setError(error.message);
                this.showError('Failed to delete resource');
                return { success: false, error: error.message };
            }
        },

        // Delete confirmation dialog methods
        confirmDelete(resource) {
            this.resourceToDelete = resource;
            this.showDeleteDialog = true;
        },

        cancelDelete() {
            this.showDeleteDialog = false;
            this.resourceToDelete = null;
        },

        async confirmDeleteResource() {
            if (!this.resourceToDelete) return;

            try {
                const result = await this.handleResourceDelete(this.resourceToDelete.id);
                if (result.success) {
                    this.showDeleteDialog = false;
                    this.resourceToDelete = null;
                }
            } catch (error) {
                // Error handling is already done in handleResourceDelete
            }
        },

        // Edit resource dialog methods
        handleEdit(resource) {
            this.resourceToEdit = resource;
            this.showEditDialog = true;
        },

        async handleUpdateResource({ resourceId, updateData }) {
            try {
                const response = await this.$axios.put(`/api/personal_resources/${resourceId}`, updateData);

                if (response.data.success) {
                    // Update the resource in the store
                    this.updatePersonalResource({ resourceId, updatedData: response.data.data });

                    // Close dialog and show success message
                    this.showEditDialog = false;
                    this.resourceToEdit = null;
                    this.showSuccess('Resource updated successfully');

                } else {
                    throw new Error(response.data.message || 'Failed to update resource');
                }
            } catch (error) {
                console.error('Error updating resource:', error);

                if (error.response && error.response.data && error.response.data.errors) {
                    // Handle validation errors
                    const errorMessages = Object.values(error.response.data.errors).flat();
                    this.showError(errorMessages.join('. '));
                } else {
                    this.showError(error.message || 'Failed to update resource. Please try again.');
                }
            }
        },

        clearFilters() {
            this.searchTerm = '';
            this.selectedCategory = 'All';
            this.selectedCompany = 'all';
            this.showPinnedOnly = false;
        },
        async refreshData() {
            this.refreshing = true;
            try {
                await Promise.all([
                    this.refreshPersonalResources(),
                    this.refreshCompanies(),
                ]);
                this.showSuccess('Data refreshed successfully');
            } catch (error) {
                console.error('Error refreshing data:', error);
                this.showError('Failed to refresh data');
            } finally {
                this.refreshing = false;
            }
        },
        showSuccess(message) {
            this.successMessage = message;
            this.showSuccessSnackbar = true;
        },
        showError(message) {
            this.errorMessage = message;
            this.showErrorSnackbar = true;
        },
        clearErrorMessage() {
            this.clearError();
        }
    }
};
</script>

<style scoped>
.resource-library {
    background-color: #fafafa;
    min-height: 100vh;
}

.upload-btn {
    font-weight: 600;
    text-transform: none;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.search-field>>>.v-input__slot {
    border-radius: 8px;
}

.company-select>>>.v-input__slot {
    border-radius: 8px;
}

.pinned-switch {
    margin-top: 0;
    padding-top: 0;
}

.clear-filters-btn {
    text-transform: none;
    color: #666;
}

.category-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.category-toggle {
    background: transparent;
    box-shadow: none;
}

.category-btn {
    text-transform: none;
    font-weight: 500;
    border-radius: 20px;
    border: 1px solid #e0e0e0;
    background: white;
}

.category-btn.v-btn--active {
    background: #1976d2 !important;
    color: white !important;
    border-color: #1976d2;
}

/* Mobile responsiveness */
@media (max-width: 960px) {
    .category-filters {
        justify-content: center;
    }

    .upload-btn {
        width: 100%;
        margin-top: 16px;
    }
}

@media (max-width: 600px) {
    .category-btn {
        font-size: 12px;
        min-width: auto;
        padding: 0 12px;
    }
}

/* Group header styling */
.group-header {
    border-bottom: 2px solid #f5f5f5;
    padding-bottom: 12px;
    margin-bottom: 24px;
}

.group-divider {
    margin: 32px 0;
    border-color: #e0e0e0;
}
</style>